import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { adminClientsService, type ClientDomain } from '../../../services/adminClientsService';
import {
  adminMiradorService,
  type MiradorConfigRead,
  type MiradorDomainEntry,
} from '../../../services/adminMiradorService';
import { toastError, toastSuccess } from '../../../lib/toast';
import { ConfirmModal } from '../../ui';
import type { AutonomousTabHandle } from './IdentityTab';
import { SystemTenantNotice } from './SystemTenantNotice';

interface Props {
  clientId: string;
  isSystem?: boolean;
}

interface LocalState {
  enabled: boolean;
  database_url: string;          // vide = inchangé si on a déjà une config persistée
  domains: Record<string, MiradorDomainEntry>;
}

const DEFAULT_STATE: LocalState = {
  enabled: false,
  database_url: '',
  domains: {},
};

const DEFAULT_TABLE = 'Table_DocImmo_Result';

export const IntegrationsTab = forwardRef<AutonomousTabHandle, Props>(({ clientId, isSystem = false }, ref) => {
  const [config, setConfig] = useState<MiradorConfigRead | null>(null);
  const [state, setState] = useState<LocalState>(DEFAULT_STATE);
  const [baseline, setBaseline] = useState<LocalState>(DEFAULT_STATE);
  const [tenantDomains, setTenantDomains] = useState<ClientDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [domainToAdd, setDomainToAdd] = useState<string>('');

  const isDirty = JSON.stringify(state) !== JSON.stringify(baseline);

  useImperativeHandle(ref, () => ({
    getIsDirty: () => isDirty,
    reset: () => setState(baseline),
  }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [cfg, doms] = await Promise.all([
          adminMiradorService.getMiradorConfig(clientId),
          adminClientsService.listDomains(clientId),
        ]);
        if (cancelled) return;
        setConfig(cfg);
        setTenantDomains(doms);
        const local: LocalState = {
          enabled: cfg.enabled,
          database_url: '',
          domains: cfg.domains ?? {},
        };
        setState(local);
        setBaseline(local);
      } catch (err) {
        if (!cancelled) toastError('Chargement config Mirador échoué', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const domainsRemaining = tenantDomains
    .filter((d) => d.is_active)
    .filter((d) => !(d.domain_code in state.domains))
    .map((d) => d.domain_code);

  const handleAddDomain = () => {
    if (!domainToAdd) return;
    setState((s) => ({
      ...s,
      domains: { ...s.domains, [domainToAdd]: { table_name: DEFAULT_TABLE, enabled: true } },
    }));
    setDomainToAdd('');
  };

  const handleRemoveDomain = (code: string) => {
    setState((s) => {
      const next = { ...s.domains };
      delete next[code];
      return { ...s, domains: next };
    });
  };

  const handleDomainChange = (code: string, patch: Partial<MiradorDomainEntry>) => {
    setState((s) => ({
      ...s,
      domains: { ...s.domains, [code]: { ...s.domains[code], ...patch } },
    }));
  };

  const handleSave = async () => {
    if (!state.database_url && !config?.configured) {
      toastError('database_url requis pour la première configuration');
      return;
    }
    if (Object.keys(state.domains).length === 0) {
      toastError('Au moins un domaine doit être configuré pour le push Mirador');
      return;
    }
    setSaving(true);
    try {
      if (!state.database_url) {
        toastError(
          'Veuillez ressaisir le DSN complet pour enregistrer (le DSN actuel n\'est pas exposé).',
        );
        setSaving(false);
        return;
      }
      await adminMiradorService.putMiradorConfig(clientId, {
        database_url: state.database_url,
        enabled: state.enabled,
        domains: state.domains,
      });
      toastSuccess('Configuration Mirador enregistrée');
      const cfg = await adminMiradorService.getMiradorConfig(clientId);
      setConfig(cfg);
      const refreshed: LocalState = {
        enabled: cfg.enabled,
        database_url: '',
        domains: cfg.domains ?? {},
      };
      setState(refreshed);
      setBaseline(refreshed);
    } catch (err) {
      toastError('Enregistrement échoué', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = state.database_url
        ? await adminMiradorService.testMirador(clientId, state.database_url)
        : await adminMiradorService.testMirador(clientId);
      if (result.ok) {
        toastSuccess(`Connexion OK (${result.latency_ms} ms)`);
      } else {
        toastError(`Échec : ${result.error ?? 'erreur inconnue'}`);
      }
    } catch (err) {
      toastError('Test échoué', err);
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminMiradorService.deleteMiradorConfig(clientId);
      toastSuccess('Configuration Mirador supprimée');
      setConfig({ configured: false, enabled: false, domains: {}, database_url_masked: null });
      setState(DEFAULT_STATE);
      setBaseline(DEFAULT_STATE);
      setConfirmDeleteOpen(false);
    } catch (err) {
      toastError('Suppression échouée', err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500 py-8 text-center text-sm">Chargement…</div>;
  }

  const domainEntries = Object.entries(state.domains);

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Tenant système : le push Mirador cible la base d'un client externe pour
          ses extractions validées ; dokimo-system héberge les GT/benchmarks
          internes et n'a rien à pousser. Formulaire affiché mais désactivé
          (cohérent avec les onglets Domaines & Configs / Utilisateurs). */}
      {isSystem && <SystemTenantNotice what="intégration Mirador non applicable (aucune extraction client à pousser)" />}

      <div className="text-sm text-gray-600">
        Push des extractions validées de ce tenant vers MySQL Mirador (PROD1.8).
        Chaque domaine cible sa propre table — typiquement <code>Table_DocImmo_Result</code>{' '}
        pour <code>acte_vente</code>.
      </div>

      <div className="flex items-center gap-2">
        <input
          id="mirador-enabled"
          type="checkbox"
          checked={state.enabled}
          disabled={isSystem}
          onChange={(e) => setState((s) => ({ ...s, enabled: e.target.checked }))}
        />
        <label htmlFor="mirador-enabled" className="text-sm font-medium text-gray-700">
          Activer le push Mirador pour ce tenant (interrupteur global)
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          DSN MySQL Mirador
        </label>
        {config?.configured && config.database_url_masked && (
          <div className="text-xs text-gray-500 mb-1 font-mono">
            Actuel : <code>{config.database_url_masked}</code>
          </div>
        )}
        <input
          type="text"
          value={state.database_url}
          disabled={isSystem}
          onChange={(e) => setState((s) => ({ ...s, database_url: e.target.value }))}
          placeholder="mysql+pymysql://user:password@host:3306/database?ssl=true"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono disabled:bg-gray-100 disabled:text-gray-400"
        />
        <p className="text-xs text-gray-500 mt-1">
          Le DSN actuel n'est pas exposé en clair. Ressaisir pour modifier. Pour DigitalOcean
          Managed MySQL, ajouter <code>?ssl=true</code> à la fin.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tables cibles par domaine
        </label>

        {domainEntries.length === 0 ? (
          <div className="text-xs text-gray-500 italic mb-2">
            Aucun domaine configuré. Ajoutez-en au moins un ci-dessous.
          </div>
        ) : (
          <div className="border border-gray-200 rounded overflow-hidden mb-3">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Domaine</th>
                  <th className="text-left px-3 py-2">Table MySQL</th>
                  <th className="text-center px-3 py-2">Actif</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {domainEntries.map(([code, entry]) => (
                  <tr key={code} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-mono text-xs">{code}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={entry.table_name}
                        disabled={isSystem}
                        onChange={(e) =>
                          handleDomainChange(code, { table_name: e.target.value })
                        }
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono disabled:bg-gray-100 disabled:text-gray-400"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={entry.enabled}
                        disabled={isSystem}
                        onChange={(e) =>
                          handleDomainChange(code, { enabled: e.target.checked })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleRemoveDomain(code)}
                        disabled={isSystem}
                        className="text-xs text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {domainsRemaining.length > 0 ? (
          <div className="flex items-center gap-2">
            <select
              value={domainToAdd}
              onChange={(e) => setDomainToAdd(e.target.value)}
              disabled={isSystem}
              className="border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">Choisir un domaine…</option>
              {domainsRemaining.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddDomain}
              disabled={!domainToAdd || isSystem}
              className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
            >
              Ajouter
            </button>
          </div>
        ) : tenantDomains.filter((d) => d.is_active).length === 0 ? (
          <div className="text-xs text-amber-700">
            ⚠ Aucun domaine actif sur ce tenant — configurer d'abord les domaines.
          </div>
        ) : (
          <div className="text-xs text-gray-500 italic">
            Tous les domaines actifs de ce tenant sont déjà configurés.
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-gray-200 flex items-center gap-2">
        <button
          type="button"
          onClick={handleTest}
          disabled={testing || isSystem}
          className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
        >
          {testing ? 'Test en cours…' : 'Tester la connexion'}
        </button>
        {config?.configured && (
          <button
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={isSystem}
            className="px-4 py-2 text-sm text-red-700 border border-red-300 rounded hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Désactiver complètement
          </button>
        )}
      </div>

      <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-gray-200 flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving || isSystem}
          className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      <ConfirmModal
        open={confirmDeleteOpen}
        title="Désactiver le push Mirador"
        message="Cette action supprime complètement la configuration Mirador du tenant. Les push à venir ne seront plus enregistrés dans la table MySQL. Continuer ?"
        confirmLabel={deleting ? 'Suppression…' : 'Désactiver'}
        cancelLabel="Annuler"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
    </div>
  );
});

IntegrationsTab.displayName = 'IntegrationsTab';

export default IntegrationsTab;
